import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from '@bbkShared/react-router';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { useCreatePagesListUrl, useUrl } from '@bbkAdminUtils/nav';
import { MainContent } from '@bbkAdminComponents/app-layout/main-content';
import { NotFoundPage } from '@bbkAdminComponents/not-found.page';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { useRoles } from '@bbkAdminUtils/roles';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import type { ReactFCC } from '@bbkAdminUtils/utility-types';
import { usePendo } from '@bbkAdminUtils/usePendo';

const Install = lazy(() => import('@bbkAdminComponents/settings/install'));
const Dashboard = lazy(() => import('@bbkAdminComponents/dashboard/dashboard'));
const Settings = lazy(() => import('@bbkAdminComponents/settings/settings'));
const Integrations = lazy(
  () => import('@bbkAdminComponents/settings/integrations')
);
const Setup = lazy(() => import('@bbkAdminComponents/settings/setup'));
const Developers = lazy(
  () => import('@bbkAdminComponents/settings/developers')
);
const Permissions = lazy(
  () => import('@bbkAdminComponents/settings/permissions')
);
const Users = lazy(() => import('@bbkAdminComponents/settings/users'));
const Plans = lazy(() => import('@bbkAdminComponents/settings/plans/plans'));

const PlayManager = lazy(
  () => import('@bbkAdminComponents/play/plays-manager')
);

const PlayEditor = lazy(() => import('@bbkAdminComponents/play/play-editor'));

const InviteUsers = lazy(
  () => import('@bbkAdminComponents/settings/invite-users')
);
const AttributionInsights = lazy(
  () => import('@bbkAdminComponents/insights/attribution-insights-report')
);
const TrendsReportGeneral = lazy(
  () => import('@bbkAdminComponents/trends-report/trends-report-general')
);
const CustomersPage = lazy(
  () => import('@bbkAdminComponents/customers/customers-page')
);
const CustomerDetailsPage = lazy(
  () => import('@bbkAdminComponents/customers/customer-details.page')
);
const DataManagement = lazy(
  () => import('@bbkAdminComponents/data-management/data-management')
);
const ConfirmationPages = lazy(
  () =>
    import(
      '@bbkAdminComponents/experiences/funnels-management/cancel-confirmation-funnels/confirmation-pages'
    )
);
const ConfirmationPageDesigner = lazy(
  () =>
    import(
      '@bbkAdminComponents/experiences/funnels-management/cancel-confirmation-funnels/confirmation-page'
    )
);
const PagesListPage = lazy(
  () =>
    import('@bbkAdminComponents/experiences/funnels-management/pages-list-page')
);
const AudiencesManagementPage = lazy(
  () => import('@bbkAdminComponents/experiences/audiences/audiences-page')
);
const LossAversionListPage = lazy(
  () =>
    import(
      '@bbkAdminComponents/experiences/loss-aversion/loss-aversions-list-page'
    )
);
const LossAversionNewPage = lazy(
  () =>
    import(
      '@bbkAdminComponents/experiences/loss-aversion/new-loss-aversion-page'
    )
);
const OffersManagementPage = lazy(
  () => import('@bbkAdminComponents/experiences/offers/offers-management-page')
);
const OffersEditOfferPage = lazy(
  () => import('@bbkAdminComponents/experiences/offers/edit-offer-page')
);
const Designer = lazy(
  () => import('@bbkAdminComponents/designer/designer-page')
);
const ReasonsEditorPage = lazy(
  () => import('@bbkAdminComponents/experiences/funnel/reasons-editor-page')
);
const SubscriptionsPage = lazy(
  () => import('@bbkAdminComponents/subscriptions-page/subscriptions-page')
);
const AlertEditPage = lazy(
  () => import('@bbkAdminComponents/subscriptions-page/alerts/alert-edit-page')
);
const WebhookEditPage = lazy(
  () =>
    import('@bbkAdminComponents/subscriptions-page/webhooks/webhook-edit-page')
);
const ActivityLogPage = lazy(
  () => import('@bbkAdminComponents/activity-log/activity-log-page')
);
const OfferPerformance = lazy(
  () => import('@bbkAdminComponents/offer-performance/offer-performance-v2')
);
const ExperiencePerformanceReportV2 = lazy(
  () =>
    import(
      '@bbkAdminComponents/experience-performance-report/exp-perf-report-v2'
    )
);

const RetainedRevenueCohortReport = lazy(
  () =>
    import(
      '@bbkAdminComponents/retained-revenue-cohort-report/retained-revenue-cohort-report'
    )
);

const LiftDashboard = lazy(
  () => import('@bbkAdminComponents/lift-dashboard/lift-dashboard')
);

const ExperiencePreview = lazy(
  () => import('@bbkAdminComponents/designer/experience-preview')
);
const ExperienceFullPreview = lazy(
  () => import('@bbkAdminComponents/designer/experience-full-preview')
);
const TargetingPage = lazy(
  () => import('@bbkAdminComponents/experiences/targeting/targeting-page')
);
const NewEditRulePage = lazy(
  () =>
    import(
      '@bbkAdminComponents/experiences/audiences/edit-rule/new-edit-rule-page'
    )
);
const BrandingPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/branding')
);
const BrandListPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/brand-list')
);

const BrandDetailsPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/brand-details')
);

const NewBrandingPage = lazy(
  () => import('@bbkAdminComponents/experiences/branding/branding-drawer')
);

const GetStartedPage = lazy(
  () => import('@bbkAdminComponents/get-started/get-started-page')
);
const GetStartedPageCreateExperience = lazy(
  () => import('@bbkAdminComponents/get-started/create-experience-page')
);
const CustomDomain = lazy(
  () => import('@bbkAdminComponents/settings/custom-domain')
);

const CustomDomainNew = lazy(
  () => import('@bbkAdminComponents/settings/custom-domain-new')
);

const PredictiveCustomerPreview = lazy(
  () => import('@bbkAdminComponents/predictive/customers-preview')
);

const baseAuthPath = '/company/:companyKey/app/:appKey';

export const AuthorizedRouter: ReactFCC = () => {
  const history = useHistory();
  const createUrl = useUrl();

  const companyKey = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const { hasBillingRole } = useRoles();

  usePendo();

  const enableAdminUsersPageOverride = useFeatureFlags(
    FeatureFlagsE.SHOW_ADMIN_USERS_PAGE_OVERRIDE
  );

  const enableVanityDomainUIForMultiBrand = useFeatureFlags(
    FeatureFlagsE.ENABLE_VANITY_DOMAIN_UI_V2
  );
  const isMultiBrandEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );

  const enableVanityDomainNewFlow = useFeatureFlags(
    FeatureFlagsE.VANITY_DOMAIN_MIGRATED_TO_NEW_FLOW
  );

  const isBillingAdmin = hasBillingRole || enableAdminUsersPageOverride;

  const isPlayEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_LIFECYCLE_OFFERS);

  const isPlayReportingEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LIFECYCLE_REPORTS
  );
  const isPredictiveEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_PREDICTIVE_DASHBOARD
  );
  const audiencesManagementUrl = createUrl(AuthorizedPaths.audiencesList);

  const { isLoading: isFFLoading } = useQueryFeatureFlags();

  const pagesListUrl = useCreatePagesListUrl();

  if (isFFLoading) return <SuspenseLoader message=" " />;

  return (
    <Switch>
      <Route
        path="/company"
        exact
        render={() => <Redirect to={`/company/${companyKey}/`} />}
      />
      <Route
        path="/company/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/`} />}
      />
      <Route
        path="/company/sign-in"
        exact
        render={() => <Redirect to={`/company/${companyKey}/`} />}
      />
      <Route
        path="/company/:companyKey/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/app/`} />}
      />
      <Route
        path="/company/:companyKey/app/"
        exact
        render={() => <Redirect to={`/company/${companyKey}/app/${appKey}/`} />}
      />
      <Route
        path={`${baseAuthPath}/`}
        exact
        render={() => <Redirect to={createUrl(AuthorizedPaths.dashboard)} />}
      />
      <Route path={`${baseAuthPath}${AuthorizedPaths.dashboard}`} exact>
        <MainContent>
          <Dashboard />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.getStarted}`} exact>
        <MainContent>
          <GetStartedPage />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep1Branding}`,
          `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep2Offers}`,
          `${baseAuthPath}${AuthorizedPaths.getStartedCreateExperienceStep3Targeting}`,
        ]}
        exact
      >
        <MainContent>
          <GetStartedPageCreateExperience />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.experiencesFunnelPreview}`,
          `${baseAuthPath}${AuthorizedPaths.experiencesHostedPreview}`,
        ]}
        exact
      >
        <MainContent>
          <ExperiencePreview />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.experiencesFunnelPreviewFull}`,
          `${baseAuthPath}${AuthorizedPaths.experiencesHostedPreviewFull}`,
        ]}
        exact
      >
        <MainContent>
          <ExperienceFullPreview />
        </MainContent>
      </Route>

      {/* Play */}
      {isPlayEnabled && (
        <Route path={`${baseAuthPath}${AuthorizedPaths.plays}`} exact>
          <MainContent>
            <PlayManager />
          </MainContent>
        </Route>
      )}
      {isPlayEnabled && (
        <Route
          path={[
            `${baseAuthPath}${AuthorizedPaths.playEdit}`,
            `${baseAuthPath}${AuthorizedPaths.playNew}`,
          ]}
          exact
        >
          <MainContent>
            <PlayEditor editMode />
          </MainContent>
        </Route>
      )}
      {isPlayEnabled && isPlayReportingEnabled && (
        <Route path={`${baseAuthPath}${AuthorizedPaths.playReports}`} exact>
          <MainContent>
            <PlayEditor editMode={false} />
          </MainContent>
        </Route>
      )}

      {/* Experiences */}
      <Route
        path={`${baseAuthPath}/experiences`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.experiencesBrandingPage)} />
        )}
      />
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.experiencesManager}`}
        exact
        render={() => <Redirect to={pagesListUrl} />}
      />
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.experiencesBrandingPage}`}
        exact
      >
        <MainContent>
          <BrandingPage />
        </MainContent>
      </Route>

      <Route
        path={`${baseAuthPath}${AuthorizedPaths.additionalBrandsPage}`}
        exact
      >
        <MainContent>
          <BrandListPage />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.createAdditionalBrand}`,
          `${baseAuthPath}${AuthorizedPaths.viewAdditionalBrand}`,
        ]}
        exact
      >
        <MainContent>
          <BrandDetailsPage />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.editAdditionalBranding}`}
        exact
      >
        <MainContent>
          <NewBrandingPage />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.experiencesPages}`} exact>
        <MainContent>
          <PagesListPage />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.confirmationPages}`} exact>
        <MainContent>
          <ConfirmationPages />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.confirmationPagesCreate}`}
        exact
      >
        <MainContent>
          <ConfirmationPageDesigner />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.confirmationPagesEdit}`}
        exact
      >
        <MainContent>
          <ConfirmationPageDesigner />
        </MainContent>
      </Route>

      <Route // Targeting
        path={`${baseAuthPath}${AuthorizedPaths.experiencesTargeting}`}
        exact
      >
        <MainContent>
          <TargetingPage />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.audiencesList}`} exact>
        <MainContent>
          <AudiencesManagementPage />
        </MainContent>
      </Route>

      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.audiencesNew}`,
          `${baseAuthPath}${AuthorizedPaths.audiencesEdit}`,
        ]}
        exact
      >
        <MainContent>
          <NewEditRulePage
            open
            onClose={() => history.push(audiencesManagementUrl)}
            onSave={() => history.push(audiencesManagementUrl)}
          />
        </MainContent>
      </Route>

      <Route
        path={`${baseAuthPath}${AuthorizedPaths.experiencesBrandManager}`}
        exact
      >
        <MainContent>
          <PagesListPage />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPage}`,
          `${baseAuthPath}${AuthorizedPaths.experiencesHostedPage}`,
        ]}
        exact
      >
        <MainContent>
          <Designer />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPageConfirmation}`}
        exact
      >
        <MainContent>
          <Designer />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.experiencesFunnelsPageReasons}`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.experiencesPageReasons)} />
        )}
      />
      <Route path={`${baseAuthPath}${AuthorizedPaths.experiencesPageReasons}`}>
        <MainContent>
          <ReasonsEditorPage />
        </MainContent>
      </Route>

      <Route path={`${baseAuthPath}${AuthorizedPaths.lossAversionNew}`} exact>
        <MainContent>
          <LossAversionNewPage />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.lossAversionList}`,
          `${baseAuthPath}${AuthorizedPaths.lossAversionEdit}`,
          `${baseAuthPath}${AuthorizedPaths.lossAversionPlacement}`,
        ]}
        exact
      >
        <MainContent>
          <LossAversionListPage />
        </MainContent>
      </Route>

      {/* Offers */}
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.offers}`,
          `${baseAuthPath}${AuthorizedPaths.offersNew}`,
        ]}
        exact
      >
        <MainContent>
          <OffersManagementPage />
        </MainContent>
      </Route>

      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.offersEdit}`,
          `${baseAuthPath}${AuthorizedPaths.offersPlacement}`,
          `${baseAuthPath}${AuthorizedPaths.offersBilling}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditDesign}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditActivities}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditItems}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditSettings}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditCtaSettings}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditCtaState}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditAITopLevel}`,
          `${baseAuthPath}${AuthorizedPaths.offersEditBranding}`,
          `${baseAuthPath}${AuthorizedPaths.offerPosition}`,
        ]}
        exact
      >
        <MainContent>
          <OffersEditOfferPage />
        </MainContent>
      </Route>

      {/* Trends */}
      <Route path={`${baseAuthPath}${AuthorizedPaths.trendsGraphs}`} exact>
        <MainContent>
          <TrendsReportGeneral />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}/trends/:trendsPage`}
        exact
        render={() => (
          <Redirect to={createUrl(AuthorizedPaths.trendsCancels)} />
        )}
      />

      {/* Insights */}
      <Route path={`${baseAuthPath}${AuthorizedPaths.insightsCancelAI}`} exact>
        <MainContent>
          <AttributionInsights isAI />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.insightsCancel}`} exact>
        <MainContent>
          <AttributionInsights />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.insightsExperiencePerformanceReport}`}
        exact
      >
        <MainContent>
          <ExperiencePerformanceReportV2 />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.insightsOfferPerformance}`}
        exact
      >
        <MainContent>
          <OfferPerformance />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.insightsRetainedRevenueCohort}`}
        exact
      >
        <MainContent>
          <RetainedRevenueCohortReport />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.insightsLiftDashboard}`}
        exact
      >
        <MainContent>
          <LiftDashboard />
        </MainContent>
      </Route>

      {/* Customers */}
      <Route path={`${baseAuthPath}${AuthorizedPaths.customers}`} exact>
        <MainContent>
          <CustomersPage />
        </MainContent>
      </Route>
      {/* Customer Details */}
      <Route path={`${baseAuthPath}${AuthorizedPaths.customersDetail}`}>
        <MainContent>
          <CustomerDetailsPage />
        </MainContent>
      </Route>

      {/* Settings */}
      <Route path={`${baseAuthPath}${AuthorizedPaths.settings}`} exact>
        <MainContent>
          <Settings />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsSubscriptions}`}
        exact
      >
        <MainContent>
          <SubscriptionsPage />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.settingsAlerts}`} exact>
        <MainContent>
          <AlertEditPage />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.settingsWebhooks}`} exact>
        <MainContent>
          <WebhookEditPage />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsIntegrations}`}
        exact
      >
        <MainContent>
          <Integrations />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.settingsSetup}`} exact>
        <MainContent>
          <Setup />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsDevelopers}`}
        exact
      >
        <MainContent>
          <Developers />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsPermissions}`}
        exact
      >
        <MainContent>
          <Permissions />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsRestrictedDomainsManage}`}
        exact
      >
        <MainContent>
          <Users />
        </MainContent>
      </Route>
      <Route
        path={[
          `${baseAuthPath}${AuthorizedPaths.settingsCustomDomain}`,
          `${baseAuthPath}${AuthorizedPaths.settingsCustomDomainNew}`,
          `${baseAuthPath}${AuthorizedPaths.settingsCustomDomainStatusPage}`,
        ]}
        exact
      >
        {(!isMultiBrandEnabled && enableVanityDomainNewFlow) ||
        (isMultiBrandEnabled &&
          (enableVanityDomainUIForMultiBrand || enableVanityDomainNewFlow)) ? (
          <CustomDomainNew />
        ) : (
          <CustomDomain />
        )}
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsFieldMappings}`}
        exact
      >
        <MainContent>
          <DataManagement />
        </MainContent>
      </Route>
      <Route path={`${baseAuthPath}${AuthorizedPaths.settingsInstall}`} exact>
        <MainContent>
          <Install />
        </MainContent>
      </Route>
      {isBillingAdmin && (
        <Route path={`${baseAuthPath}${AuthorizedPaths.settingsUsers}`} exact>
          <MainContent>
            <Users />
          </MainContent>
        </Route>
      )}
      <Route path={[`${baseAuthPath}${AuthorizedPaths.settingsPlans}`]} exact>
        <MainContent>
          <Plans />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsUsersInvite}`}
        exact
      >
        <MainContent>
          <InviteUsers />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settingsActivityLog}`}
        exact
      >
        <MainContent>
          <ActivityLogPage />
        </MainContent>
      </Route>
      <Route
        path={`${baseAuthPath}${AuthorizedPaths.settings}`}
        render={() => <Redirect to={createUrl(AuthorizedPaths.settings)} />}
      />
      {/* Predictive */}
      {isPredictiveEnabled && (
        <Route
          path={`${baseAuthPath}${AuthorizedPaths.viewPredictiveCustomerPreview}`}
          exact
        >
          <MainContent>
            <PredictiveCustomerPreview />
          </MainContent>
        </Route>
      )}
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
};
